import { supabase } from "@/integrations/supabase/client";

/**
 * Offline-stöd i fält: köar skrivningar lokalt när nätet är borta
 * och synkar automatiskt när uppkopplingen kommer tillbaka.
 * Läsningar cachas lokalt så att skyddsronder och tillbud går att
 * öppna även utan täckning.
 */

const QUEUE_KEY = "amk_offline_queue_v1";
const CACHE_PREFIX = "amk_offline_cache_v1:";

export type QueueOp = "insert" | "update" | "delete";

export interface QueuedMutation {
  id: string;
  table: string;
  op: QueueOp;
  /** rad-id för update/delete */
  rowId?: string;
  values?: Record<string, unknown>;
  /** flera rader vid insert */
  rows?: Record<string, unknown>[];
  createdAt: number;
}

type Listener = (count: number) => void;
const listeners = new Set<Listener>();

function read(): QueuedMutation[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]") as QueuedMutation[];
  } catch {
    return [];
  }
}

function write(queue: QueuedMutation[]) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    /* full storage – ignorera */
  }
  listeners.forEach((l) => l(queue.length));
}

export function pendingCount(): number {
  return read().length;
}

export function subscribePending(listener: Listener): () => void {
  listeners.add(listener);
  listener(pendingCount());
  return () => listeners.delete(listener);
}

export function isOnline(): boolean {
  return typeof navigator === "undefined" ? true : navigator.onLine !== false;
}

export function newLocalId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function enqueue(mutation: Omit<QueuedMutation, "id" | "createdAt">) {
  const queue = read();
  queue.push({ ...mutation, id: newLocalId(), createdAt: Date.now() });
  write(queue);
}

/** Cachea en lista lokalt (t.ex. skyddsronder för ett projekt). */
export function cacheSet<T>(key: string, value: T) {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    /* ignorera */
  }
}

export function cacheGet<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/**
 * Skriv till backend om vi är online, annars lägg i kön.
 * Returnerar true om ändringen köades (dvs. inte sparad i molnet ännu).
 */
export async function offlineInsert(
  table: string,
  rows: Record<string, unknown> | Record<string, unknown>[]
): Promise<{ queued: boolean; error?: string }> {
  const list = Array.isArray(rows) ? rows : [rows];
  if (!isOnline()) {
    enqueue({ table, op: "insert", rows: list });
    return { queued: true };
  }
  const { error } = await supabase.from(table as never).insert(list as never);
  if (error) {
    enqueue({ table, op: "insert", rows: list });
    return { queued: true, error: error.message };
  }
  return { queued: false };
}

export async function offlineUpdate(
  table: string,
  rowId: string,
  values: Record<string, unknown>
): Promise<{ queued: boolean; error?: string }> {
  if (!isOnline()) {
    enqueue({ table, op: "update", rowId, values });
    return { queued: true };
  }
  const { error } = await supabase.from(table as never).update(values as never).eq("id", rowId);
  if (error) {
    enqueue({ table, op: "update", rowId, values });
    return { queued: true, error: error.message };
  }
  return { queued: false };
}

export async function offlineDelete(
  table: string,
  rowId: string
): Promise<{ queued: boolean; error?: string }> {
  if (!isOnline()) {
    enqueue({ table, op: "delete", rowId });
    return { queued: true };
  }
  const { error } = await supabase.from(table as never).delete().eq("id", rowId);
  if (error) {
    enqueue({ table, op: "delete", rowId });
    return { queued: true, error: error.message };
  }
  return { queued: false };
}

let flushing = false;

/** Skickar upp allt som ligger i kön. Returnerar antal synkade poster. */
export async function flushQueue(): Promise<number> {
  if (flushing || !isOnline()) return 0;
  flushing = true;
  let synced = 0;
  try {
    let queue = read();
    while (queue.length > 0) {
      const item = queue[0];
      let error: { message: string } | null = null;

      if (item.op === "insert" && item.rows) {
        ({ error } = await supabase.from(item.table as never).insert(item.rows as never));
      } else if (item.op === "update" && item.rowId) {
        ({ error } = await supabase
          .from(item.table as never)
          .update(item.values as never)
          .eq("id", item.rowId));
      } else if (item.op === "delete" && item.rowId) {
        ({ error } = await supabase.from(item.table as never).delete().eq("id", item.rowId));
      }

      if (error) {
        // Nätfel: avbryt och behåll kön. Datafel: släng posten så kön inte fastnar.
        if (!isOnline()) break;
        console.error("Offline-synk misslyckades", item.table, error.message);
      }

      queue = read().filter((q) => q.id !== item.id);
      write(queue);
      synced += 1;
    }
  } finally {
    flushing = false;
  }
  return synced;
}
